import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Grid, Lightformer, OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import DiamondSkyModel from "../DiamondSkyModel";
import { FlowPipe } from "./FlowPipe";
import { StorageTank, IndustrialPlant } from "./PlantEquipment";
import { Hotspot, HotspotContext, HotspotOverlay } from "./Hotspot";
import { advanceSimulation } from "./simulation";

const INDUSTRY_HOT = [[1.95, 2.7, 0.3], [2.7, 2.7, 1.8], [3.5, 1.2, 1.8], [4.05, 1.2, 1.3]];
const INDUSTRY_COLD = [[4.05, 0.65, 1.3], [3.4, 0.45, 2.3], [2.5, 0.45, 2.3], [1.95, 0.7, 0.3]];

function SimulationClock({ sim, solar, demand, running, onTick }) {
  const publish = useRef(0);
  useFrame((_, delta) => {
    advanceSimulation(sim.current, delta, solar, demand, running);
    publish.current += delta;
    if (publish.current > 0.25) { onTick({ ...sim.current }); publish.current = 0; }
  }, -3);
  return null;
}

function CameraRig({ revision }) {
  const controls = useRef();
  const { camera, size } = useThree();
  useEffect(() => {
    camera.position.set(10, 8.5, 19);
    camera.zoom = Math.min(size.width / 17.5, size.height / 10.5);
    camera.lookAt(0, 2.9, 0);
    camera.updateProjectionMatrix();
    controls.current?.target.set(0, 2.9, 0);
    controls.current?.update();
  }, [camera, size.width, size.height, revision]);
  return <OrbitControls ref={controls} target={[0, 2.9, 0]} enableZoom={false} enablePan={false} minPolarAngle={0.5} maxPolarAngle={1.4} />;
}

function Circuit(props) {
  const { sim, selected, onSelect, copy } = props;
  const ports = useRef({ focus: new THREE.Vector3(-4, 4, 1), elbow: new THREE.Vector3(-3, 2, 1) });
  const hot = useCallback(() => [ports.current.focus.toArray(), ports.current.elbow.toArray(), [-4, 1.3, 1.4], [-3.2, 0.65, 1.5], [-1.5, 0.65, 1.5], [0, 2.7, 0.3]], []);
  const cold = useCallback(() => [[0, 0.7, 0.3], [-1.5, 0.38, 2], [-3.5, 0.38, 2], [-4.25, 1.3, 1.7], ports.current.elbow.clone().add(new THREE.Vector3(0, 0, 0.18)).toArray(), ports.current.focus.clone().add(new THREE.Vector3(-0.18, 0, 0)).toArray()], []);
  const selection = { selected, onSelect, copy };
  const pipeProps = { sim, running: props.running, selected: selected === "pipes", onSelect };
  return (
    <>
      <SimulationClock {...props} />
      <ambientLight intensity={0.65} /><hemisphereLight args={["#d7e8ff", "#414547", 1]} />
      <directionalLight position={[4, 10, 8]} intensity={1.5} />
      <group position={[-5, 0, 0]} scale={0.72}><DiamondSkyModel {...props} ports={ports} /></group>
      <FlowPipe getPoints={hot} color="#ff554b" {...pipeProps} />
      <FlowPipe getPoints={cold} color="#419cff" {...pipeProps} />
      <FlowPipe points={INDUSTRY_HOT} color="#ff554b" circuit="output" {...pipeProps} />
      <FlowPipe points={INDUSTRY_COLD} color="#419cff" circuit="output" {...pipeProps} />
      <Hotspot part="pipes" position={[-1.7, 1.15, 1.6]} {...selection} />
      <StorageTank sim={sim} {...selection} /><IndustrialPlant sim={sim} running={props.running} {...selection} />
      <Grid position={[0, -0.02, 0]} args={[30, 24]} cellColor="#272e30" sectionColor="#343e40" fadeDistance={26} />
      <Environment resolution={64} frames={1}>
        <mesh scale={60}><sphereGeometry args={[1, 24, 24]} /><meshBasicMaterial color="#82b5e0" side={THREE.BackSide} /></mesh>
        <Lightformer form="rect" intensity={3} position={[6, 14, 6]} scale={[10, 4, 1]} target={[0, 0, 0]} color="#ffffff" />
        <Lightformer form="rect" intensity={2} position={[-12, 9, -4]} scale={[9, 3, 1]} target={[0, 0, 0]} color="#ffffff" />
      </Environment>
      <CameraRig revision={props.cameraRevision} />
    </>
  );
}

export default memo(function ThermalScene({ visible, ...props }) {
  const camera = useMemo(() => ({ position: [10, 8.5, 19], zoom: 40, near: 0.1, far: 100 }), []);
  const nodes = useRef({});
  return (
    <HotspotContext.Provider value={nodes}>
    <Canvas orthographic camera={camera} dpr={[1, 1.5]} frameloop={visible ? "always" : "never"} data-testid="diamond-sky-3d"
      gl={{ antialias: true, preserveDrawingBuffer: true }} fallback={<p data-testid="model-webgl-fallback">{props.copy.noWebGL}</p>}>
      <color attach="background" args={["#111315"]} />
      <Circuit {...props} />
    </Canvas>
    <HotspotOverlay nodes={nodes} selected={props.selected} onSelect={props.onSelect} copy={props.copy} />
    </HotspotContext.Provider>
  );
});